// Create a new file: /components/workflow/RestructureNodeModal.jsx

import React, { useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { produce } from 'immer';
import toast from 'react-hot-toast';
import { Edit, Link2 } from 'lucide-react';
import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import { fieldsToSchema, schemaToFields } from '../NewWorkflow/utils/schemaUtils';


// --- HELPERS (SCOPED TO THIS FILE) ---
const findFieldPathById = (fields, id, currentPath = []) => {
    for (const field of fields) {
        const path = [...currentPath, field.fieldName];
        if (field.id === id) return path.join('.');
        if (field.children?.length) {
            const foundPath = findFieldPathById(field.children, id, path);
            if (foundPath) return foundPath;
        }
    }
    return null;
};

const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const deepCloneField = (field) => {
    const newField = { ...field, id: generateId() };
    if (field.children && field.children.length > 0) {
        newField.children = field.children.map(deepCloneField);
    }
    return newField;
};

// const schemaToFields = (schema, prefix) => {
//     if (!schema || !schema.properties) return [];
//     return Object.entries(schema.properties).map(([key, value]) => ({
//         id: `${prefix}${key}`,
//         fieldName: key,
//         dataType: value.type || 'string',
//         description: value.description,
//         children: (value.type === 'object' && value.properties) ? schemaToFields(value, `${prefix}${key}.`) : [],
//         arrayItemType: value.items?.type || 'String',
//     }));
// };

// // --- *** NEW & CRITICAL ***: Converts editable fields array to JSON Schema ---
// const fieldsToSchema = (fields, modelName = 'Output') => {
//     const convert = (fieldList) => {
//         const properties = {};
//         const required = [];

//         fieldList.forEach(field => {
//             if (!field.fieldName) return;
//             let schemaDef = { description: field.description };

//             const dataType = field.dataType.toLowerCase();

//             switch (dataType) {
//                 case 'string': case 'number': case 'boolean':
//                     schemaDef.type = dataType;
//                     break;
//                 case 'object':
//                     schemaDef.type = 'object';
//                     const nested = convert(field.children || []);
//                     schemaDef.properties = nested.properties;
//                     if (nested.required.length > 0) schemaDef.required = nested.required;
//                     break;
//                 case 'array':
//                     schemaDef.type = 'array';
//                     const itemType = field.arrayItemType?.toLowerCase() || 'string';
//                     if (itemType === 'object') {
//                         const nestedArray = convert(field.children || []);
//                         schemaDef.items = {
//                             type: 'object',
//                             properties: nestedArray.properties,
//                         };
//                         if (nestedArray.required.length > 0) {
//                             schemaDef.items.required = nestedArray.required;
//                         }
//                     } else {
//                         schemaDef.items = { type: itemType };
//                     }
//                     break;
//                 default:
//                     schemaDef.type = 'string';
//             }
//             properties[field.fieldName] = schemaDef;
//             if (field.required) required.push(field.fieldName);
//         });
//         return { properties, required };
//     };

//     const root = convert(fields);
//     return {
//         title: modelName,
//         type: 'object',
//         properties: root.properties,
//         ...(root.required.length > 0 && { required: root.required }),
//     };
// };


const ToolOutputNodeModal = ({ mode, onDelete, node, nodes, edges, onSave, onClose, globalState }) => {
    const [linkedToolInputId, setLinkedToolInputId] = useState(node?.data?.toolInputNodeId || '');

    const [outputFields, setOutputFields] = useState(node?.data?.outputFields || []);
    const [mappings, setMappings] = useState(node?.data?.mappings || {});
    // ... (rest of the state from the reference StructureNodeModal)
    const [selectedInputField, setSelectedInputField] = useState(null);
    const [isBuilderOpen, setIsBuilderOpen] = useState(false);

    const containerRef = useRef(null);
    const elementRefs = useRef(new Map());
    const [lines, setLines] = useState([]);

    // --- CRITICAL LOGIC: Find available Tool Input nodes to link to ---
    const availableToolInputs = useMemo(() => {
        // 1. Find all Tool Input nodes on the canvas
        const allToolInputs = nodes.filter(n => n.type === 'toolInput');

        // 2. Find which Tool Inputs are already linked by other Tool Output nodes
        const linkedInputIds = nodes
            .filter(n => n.type === 'toolOutput' && n.id !== node.id) // Exclude the current node
            .map(n => n.data.toolInputNodeId);

        // 3. Return only the unlinked ones
        return allToolInputs.filter(n => !linkedInputIds.includes(n.id));
    }, [nodes, node.id]);

    const inputRootFields = useMemo(() => {
        // return edges
        //     .filter(edge => edge.target === node.id)
        //     .map(edge => nodes.find(n => n.id === edge.source))
        //     .filter(Boolean)
        //     .map(parentNode => {
        //         let schema;
        //         // --- THIS IS THE CRITICAL LOGIC ---
        //         // Check if the parent has a defined JSON output.
        //         if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
        //             schema = parentNode.data.jsonOutputSchema;
        //         } else {
        //             // If not, create a default schema for its simple string output.
        //             schema = {
        //                 title: 'Simple Output',
        //                 type: 'object',
        //                 properties: {
        //                     'output': {
        //                         type: 'string',
        //                         description: `The plain text output from "${parentNode.data.label || parentNode.type}"`
        //                     }
        //                 }
        //             };
        //         }
        //         // --- END OF CRITICAL LOGIC ---

        //         if (!schema) return null;

        //         const inputKey = `input_${parentNode.id}`;
        //         return {
        //             id: inputKey,
        //             fieldName: parentNode.data.label || 'Input Node',
        //             dataType: 'Object',
        //             children: schemaToFields(schema, ''),
        //         };
        //     }).filter(Boolean);
        const connectedInputNodes = edges
            .filter(edge => edge.target === node.id)
            .map(edge => nodes.find(n => n.id === edge.source))
            .filter(Boolean)
            .filter(parentNode => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
            .map(parentNode => {
                let schema;
                if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
                    schema = parentNode.data.jsonOutputSchema;
                } else {
                    schema = {
                        title: 'Simple Output', type: 'object',
                        properties: { 'output': { type: 'string', description: `Output from "${parentNode.data.label || parentNode.type}"` } }
                    };
                }
                return {
                    id: `input_${parentNode.id}`, fieldName: parentNode.data.label || 'Input Node',
                    dataType: 'Object', children: schemaToFields(schema, ''),
                };
            }).filter(Boolean);

        // ⭐ 2. Create a "virtual node" for the Global State if it exists
        const globalStateNode = [];
        if (globalState && globalState.fields.length > 0) {
            const globalSchema = fieldsToSchema(globalState.fields, "globalState");
            globalStateNode.push({
                id: 'globalState', // Special, reserved ID
                fieldName: 'Global State',
                dataType: 'Object',
                children: schemaToFields(globalSchema, ''),
            });
        }

        // ⭐ 3. Combine the Global State with the other input nodes
        return [...globalStateNode, ...connectedInputNodes];
    }, [node.id, nodes, edges]);

    // ... (Paste the entire rest of the component logic from the reference StructureNodeModal.js)
    // No other changes are needed in this component.

    const outputRootFields = useMemo(() => [{
        id: 'output',
        fieldName: 'Output',
        dataType: 'Object',
        children: outputFields
    }], [outputFields]);

    const registerRef = useCallback((id, el) => {
        if (el) elementRefs.current.set(id, el);
        else elementRefs.current.delete(id);
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            const newLines = Object.entries(mappings).map(([outputFieldId, inputPathArray]) => {
                const inputPath = inputPathArray.join('.');
                const outputFieldPath = findFieldPathById(outputRootFields, outputFieldId, []);

                const startEl = elementRefs.current.get(`handle-source-${inputPath}`);
                const endEl = elementRefs.current.get(`handle-target-${outputFieldPath}`);

                if (startEl && endEl) {
                    const cRect = container.getBoundingClientRect();
                    const sRect = startEl.getBoundingClientRect();
                    const eRect = endEl.getBoundingClientRect();
                    const d = `M ${sRect.right - cRect.left} ${sRect.top + sRect.height / 2 - cRect.top} C ${sRect.right - cRect.left + 60} ${sRect.top + sRect.height / 2 - cRect.top}, ${eRect.left - cRect.left - 60} ${eRect.top + eRect.height / 2 - cRect.top}, ${eRect.left - cRect.left} ${eRect.top + eRect.height / 2 - cRect.top}`;
                    return { id: `${inputPath}-${outputFieldId}`, d };
                }
                return null;
            }).filter(Boolean);
            setLines(newLines);
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [mappings, outputRootFields]);

    const handleInputFieldClick = (path, field) => {
        setSelectedInputField({ path: path.split('.'), field });
        toast.success(`Selected input: ${field.fieldName}`);
    };

    const handleOutputFieldClick = (path, field) => {
        if (mappings[field.id]) {
            setMappings(produce(draft => { delete draft[field.id]; }));
            toast.success(`Unmapped ${field.fieldName}`);
            return;
        }
        if (!selectedInputField) {
            toast.error('Please select an input field first.');
            return;
        }
        if (selectedInputField.field.dataType.toLowerCase() !== field.dataType.toLowerCase()) {
            toast.error(`Type mismatch: Can't map ${selectedInputField.field.dataType} to ${field.dataType}.`);
            return;
        }
        setMappings(produce(draft => { draft[field.id] = selectedInputField.path; }));
        setSelectedInputField(null);
    };

    const handlePassThroughClick = (field, path) => {
        const newField = deepCloneField(field);
        if (outputFields.some(f => f.fieldName === newField.fieldName)) {
            toast.error(`Field "${newField.fieldName}" already exists in output.`);
            return;
        }
        setOutputFields(produce(draft => { draft.push(newField); }));
        setMappings(produce(draft => { draft[newField.id] = path.split('.'); }));
        toast.success(`Added & mapped "${newField.fieldName}" to output.`);
    };

    const handleSchemaSave = ({ fields }) => {
        setOutputFields(fields);
        const findAllIds = (flds) => flds.reduce((acc, f) => {
            acc.add(f.id);
            if (f.children?.length) findAllIds(f.children).forEach(id => acc.add(id));
            return acc;
        }, new Set());
        const validFieldIds = findAllIds(fields);
        setMappings(produce(draft => {
            for (const key in draft) { if (!validFieldIds.has(key)) delete draft[key]; }
        }));
    };

    const handleSaveAndClose = () => {
        // --- VALIDATION ---
        if (!linkedToolInputId) {
            toast.error("You must link this output to a Tool Input node.");
            return;
        }

        const linkedToolInputNode = nodes.find(n => n.id === linkedToolInputId);
        if (!linkedToolInputNode) {
            toast.error("The selected Tool Input node could not be found.");
            return;
        }

        const finalOutputSchema = fieldsToSchema(outputFields, "RestructuredOutput");

        onSave({
            ...node,
            data: {
                ...node.data,
                label: `Output for ${linkedToolInputNode.data.toolName}`,

                outputFields,
                jsonOutputSchema: finalOutputSchema,
                jsonOutput: true, // This node always produces a structured output
                mappings,

                // --- SAVE LINKING INFO ---
                toolInputNodeId: linkedToolInputId,
                toolInputNodeName: linkedToolInputNode.data.toolName,
            },
        });
        onClose();
    };

    // ... (rest of the component JSX)

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
                <div className="bg-slate-800 w-full max-w-7xl h-[90vh] rounded-xl shadow-2xl border border-slate-700 flex flex-col" onClick={e => e.stopPropagation()}>
                    {/* --- HEADER --- */}
                    <div className="p-4 border-b border-slate-700 flex justify-between items-center flex-shrink-0">
                        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Link2 /> Configure Tool Output</h2>
                        <button
                            onClick={() => setIsBuilderOpen(true)}
                            disabled={!linkedToolInputId}
                            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-3 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Edit size={16} /><span>Edit Output Schema</span>
                        </button>
                    </div>

                    {/* --- BODY --- */}
                    <div className="p-4 flex-shrink-0 border-b border-slate-700">
                        <label className="block text-sm font-medium text-zinc-200 mb-2">
                            Link to Tool Input Node (Required)
                        </label>
                        <select
                            value={linkedToolInputId}
                            onChange={(e) => setLinkedToolInputId(e.target.value)}
                            className="w-full max-w-sm px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="" disabled>-- Select an Input --</option>
                            {availableToolInputs.map(inputNode => (
                                <option key={inputNode.id} value={inputNode.id}>
                                    {inputNode.data.toolName || inputNode.id}
                                </option>
                            ))}
                        </select>
                        {!linkedToolInputId && (
                            <p className="text-xs text-yellow-400 mt-2">
                                You must select a Tool Input before you can map fields.
                            </p>
                        )}
                    </div>

                    {/* --- MAPPING AREA (Conditional) --- */}
                    {linkedToolInputId ? (
                        <div className="flex-grow grid grid-cols-2 gap-4 p-4 overflow-hidden relative" ref={containerRef}>
                            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
                                {lines.map(line => <path key={line.id} d={line.d} stroke="#6366f1" strokeWidth="2" fill="none" className="opacity-75" />)}
                            </svg>

                            <div className="flex flex-col gap-2 overflow-y-auto pr-2">
                                <h3 className="text-lg font-semibold text-slate-200">Input (Click to Select)</h3>
                                <SchemaViewer rootFields={inputRootFields} onFieldClick={handleInputFieldClick} onPassThroughClick={handlePassThroughClick} selectedPath={selectedInputField?.path.join('.')} registerRef={registerRef} mappings={mappings} />
                            </div>
                            <div className="flex flex-col gap-2 overflow-y-auto pl-2">
                                <h3 className="text-lg font-semibold text-slate-200">Output (Click to Map)</h3>
                                <SchemaViewer rootFields={outputRootFields} onFieldClick={handleOutputFieldClick} isOutput={true} registerRef={registerRef} mappings={mappings} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-grow flex items-center justify-center text-slate-500">
                            Select a Tool Input to begin mapping.
                        </div>
                    )}

                    {/* --- FOOTER --- */}
                    <div className="p-4 flex justify-between items-center bg-slate-800/50 border-t border-slate-700 flex-shrink-0">
                        {mode === "edit" && (
                            <button onClick={() => onDelete(node.id)} className="px-4 py-2 text-sm text-red-300 bg-red-800/50 hover:bg-red-800 rounded-md">
                                Delete Node
                            </button>
                        )}
                        <div className="flex gap-3">
                            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-md">Cancel</button>
                            <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md">Save & Close</button>
                        </div>
                    </div>
                </div>
            </div>

            <SchemaBuilderModal
                isOpen={isBuilderOpen}
                onClose={() => setIsBuilderOpen(false)}
                onSave={handleSchemaSave}
                initialFields={outputFields}
            />
        </>
    );

};

export default ToolOutputNodeModal;